/**
 * Types for the `machine_update` section of the agroNave fleet telemetry
 * (`fleet/{userId}/{deviceId}/update` topic), schema version 1.
 *
 * Protocol reference: agronave-pro wiki, "Protocolo de telemetria isoFarm (MQTT)",
 * section "Telemetria de máquina (machine_update)".
 */

export const MACHINE_UPDATE_SUPPORTED_SCHEMA_VERSION = 1;

export enum MachineLinkState {
  EngineNotDetected = 'engine_not_detected',
  Receiving = 'receiving',
  NoCommunication = 'no_communication',
}

export enum MachineSignalState {
  Valid = 'valid',
  NeverReceived = 'never_received',
  NotAvailable = 'not_available',
  Error = 'error',
  Stale = 'stale',
}

export enum MachineFaultStatus {
  Active = 'active',
  NoActiveFaults = 'no_active_faults',
  Unknown = 'unknown',
}

export enum MachineLampState {
  Off = 'off',
  On = 'on',
  SlowFlash = 'slow_flash',
  FastFlash = 'fast_flash',
  NotAvailable = 'not_available',
  Unknown = 'unknown',
}

export enum MachineLamp {
  Mil = 'mil',
  RedStop = 'red_stop',
  Amber = 'amber',
  Protect = 'protect',
}

export enum MachineLampSeverity {
  Danger = 'danger',
  Warning = 'warning',
  Ok = 'ok',
  Muted = 'muted',
}

export interface MachineUpdateEngine {
  source_address: number | null;
  name: string | null;
  manufacturer_code: number | null;
  from_fallback: boolean;
}

export interface MachineUpdateSignal {
  spn: number;
  state: MachineSignalState;
  /** Present only when `state` is `valid`. */
  raw?: number;
}

export interface MachineUpdateDtc {
  spn: number;
  fmi: number;
  occurrence_count: number | null;
  /**
   * Present, and `true`, only when the ECU set the SPN conversion method bit (SPN 1706):
   * its SPN uses a legacy J1939-73 method, so `spn` holds the bits as read and does not
   * identify a parameter. `fmi` and `occurrence_count` are still valid.
   */
  legacy_spn_conversion?: boolean;
}

export type MachineUpdateLamps = Record<MachineLamp, MachineLampState>;

export interface MachineUpdateFault {
  source_address: number;
  name: string | null;
  status: MachineFaultStatus;
  lamps: MachineUpdateLamps;
  dtcs: MachineUpdateDtc[];
  dtc_total: number;
  truncated: boolean;
}

export interface MachineUpdateMessage {
  schema_version: number;
  snapshot_age_ms: number;
  link: MachineLinkState;
  engine: MachineUpdateEngine;
  signals: MachineUpdateSignal[];
  faults: MachineUpdateFault[];
}

/** A `machine_update` section together with the instant it refers to. */
export interface MachineUpdateSnapshot {
  message: MachineUpdateMessage;
  /** `device_update.timestamp` of the message that carried the section (UTC, ms). */
  receivedAt: number;
}

export interface MachineSignalView {
  spn: number;
  labelKey: string;
  shortLabelKey: string;
  kpiLabelKey: string;
  state: MachineSignalState;
  /** Decoded value in `unit`. Only set for valid measurement signals. */
  value: number | null;
  decimals: number;
  unit: string;
  /** i18n key shown instead of a number: the signal state, or the meaning of a valid state signal. */
  textKey: string | null;
}

export interface MachineSignalGroupView {
  id: string;
  titleKey: string;
  signals: MachineSignalView[];
}

export interface MachineLampView {
  labelKey: string;
  shortLabelKey: string;
  stateKey: string;
  severity: MachineLampSeverity;
  /** On or flashing. */
  isLit: boolean;
  isFlashing: boolean;
}

export interface MachineDtcView {
  /** `null` when the ECU used a legacy SPN conversion method, so the code names no parameter. */
  spn: number | null;
  fmi: number;
  fmiKey: string;
  /** Signal name when the SPN is one of the catalog signals. */
  signalLabelKey: string | null;
  occurrenceCount: number | null;
}

export interface MachineFaultView {
  sourceAddress: number;
  isEngine: boolean;
  status: MachineFaultStatus;
  statusKey: string;
  lamps: MachineLampView[];
  dtcs: MachineDtcView[];
  /** Fault codes the ECU reported but the agroNave dropped to fit the size limit. */
  hiddenDtcCount: number;
  /** Every active code the ECU reported, shown or dropped. */
  dtcCount: number;
}

export interface MachineTelemetryView {
  isSupported: boolean;
  schemaVersion: number;
  link: MachineLinkState;
  linkKey: string;
  groups: MachineSignalGroupView[];
  /** The same groups stacked in columns, as MACHINE_SIGNAL_COLUMNS lays them out. */
  signalColumns: MachineSignalGroupView[][];
  /** Summary tiles, in the order of MACHINE_KPI_SPNS. */
  kpis: MachineSignalView[];
  engineHours: MachineSignalView | null;
  signalCount: number;
  /** Signals that are not valid, so show no value. */
  unreadSignalCount: number;
  faults: MachineFaultView[];
  /** ECUs that need a card: active faults or no recent DM1. */
  faultCards: MachineFaultView[];
  /** ECUs whose last DM1 listed no code. */
  healthyEcus: MachineFaultView[];
  /** Lamps lit on any ECU, one per lamp, most severe first. */
  litLamps: MachineLampView[];
  activeDtcCount: number;
  receivedAt: number;
  isOutdated: boolean;
}
